import { Component } from '@grafikr/components';
import { NonNullish } from 'utils/nullish-check';
import repaint from 'utils/repaint';
import { dispatch } from 'utils/theme';
import { getGender } from 'utils/gender';

type ScrollDirection = 'up' | 'down';

export default Component((node) => {
  const MODAL_OPEN_CLASS = 'header--modal-open';
  const MODAL_SCROLLED_CLASS = 'header--modal-scrolled';
  const SEARCH_OPEN_CLASS = 'header--search-open';
  const STICKY_CLASS = 'header--sticky';
  const VISIBLE_CLASS = 'header--visible';
  const NAV_ITEM_ACTIVE_CLASS = 'header__nav-item--active';
  const MEGAMENU_ACTIVE_CLASS = 'header__megamenu--active';
  const MOBILE_MENU_TOGGLE_OPEN_CLASS = 'header__mobile-menu-toggle--open';
  const ANNOUNCEMENT_BAR_HEIGHT_PROPERTY = '--announcement-bar-height';
  const HEADER_HEIGHT_PROPERTY = '--header-height';
  const SCROLL_THRESHOLD = 4;
  const MEGAMENU_DELAY = 250;

  const megamenuTriggers = Array.from(node.querySelectorAll<HTMLElement>('[data-megamenu-trigger]'));
  const megamenus = Array.from(node.querySelectorAll<HTMLElement>('[data-megamenu]'));
  const mobileMenuToggle = NonNullish(node.querySelector<HTMLElement>('[data-mobile-menu-toggle]'));

  let previousY = window.scrollY;

  const handleScroll = () => {
    const currentY = window.scrollY;
    const scrollDirection: ScrollDirection = currentY < previousY ? 'up' : 'down';

    const computedStyle = getComputedStyle(document.documentElement);
    const announcementBarHeight = parseInt(computedStyle.getPropertyValue(ANNOUNCEMENT_BAR_HEIGHT_PROPERTY));
    const headerHeight = parseInt(computedStyle.getPropertyValue(HEADER_HEIGHT_PROPERTY));

    const isBeyondAnnouncementBar = (currentY > announcementBarHeight) || (announcementBarHeight === 0 && currentY > 0);
    const isBeyondHeader = currentY > announcementBarHeight + headerHeight;

    switch (scrollDirection) {
      case 'down':
        if (isBeyondHeader && !node.classList.contains(STICKY_CLASS)) {
          node.style.transition = 'none';
          node.classList.add(STICKY_CLASS);

          setTimeout(() => {
            node.style.transition = '';
          }, 0);
        }

        if (currentY - previousY > SCROLL_THRESHOLD && !node.classList.contains(MODAL_OPEN_CLASS)) {
          node.classList.remove(VISIBLE_CLASS);
        }

        break;

      case 'up':
        if (!isBeyondAnnouncementBar) {
          node.classList.remove(STICKY_CLASS);
          node.classList.remove(VISIBLE_CLASS);
        }

        if (isBeyondHeader && previousY - currentY > SCROLL_THRESHOLD) {
          node.classList.add(VISIBLE_CLASS);
        }

        break;
    }

    previousY = currentY;
  };

  const handleHeaderModals = () => {
    document.addEventListener('header-modal:open', () => {
      node.classList.add(MODAL_OPEN_CLASS);
    });

    document.addEventListener('header-modal:close', () => {
      node.classList.remove(MODAL_OPEN_CLASS);
    });

    document.addEventListener('header-modal:scroll', ({ detail}) => {
      node.classList.toggle(MODAL_SCROLLED_CLASS, detail.scrolled);
    });
  };

  const handleSearchModal = () => {
    document.addEventListener('search-modal:open', () => {
      node.classList.add(SEARCH_OPEN_CLASS);
    });

    document.addEventListener('search-modal:close', () => {
      node.classList.remove(SEARCH_OPEN_CLASS);
    });
  };

  const initMegamenus = () => {
    megamenus.forEach((megamenu) => {
      const megamenuId = NonNullish(megamenu.dataset.megamenuId);
      const trigger = NonNullish(megamenuTriggers.find((tr) => tr.dataset.megamenuId === megamenuId));

      const onLeave = (event: MouseEvent) => {
        if (trigger.contains(event.relatedTarget as Node) || megamenu.contains(event.relatedTarget as Node)) {
          return;
        }

        setTimeout(() => {
          if (trigger.matches(':hover') || megamenu.matches(':hover')) {
            return;
          }

          const hoveredTrigger = megamenuTriggers.find((tr) => tr.matches(':hover'));
          const hoveredMegamenu = megamenus.find((m) => m.matches(':hover'));

          if (hoveredTrigger === undefined && hoveredMegamenu === undefined) {
            node.classList.remove(MODAL_OPEN_CLASS);
          }

          megamenu.classList.remove(MEGAMENU_ACTIVE_CLASS);
          trigger.classList.remove(NAV_ITEM_ACTIVE_CLASS);
          trigger.removeEventListener('mouseleave', onLeave);
          megamenu.removeEventListener('mouseleave', onLeave);

          megamenu.addEventListener('transitionend', () => {
            if (megamenu.classList.contains(MEGAMENU_ACTIVE_CLASS)) {
              return;
            }

            megamenu.hidden = true;
            trigger.ariaExpanded = 'false';
          }, { once: true });

          document.documentElement.style.overflow = '';
          document.body.style.paddingRight = '';
        }, MEGAMENU_DELAY);
      };

      trigger.addEventListener('mouseover', () => {
        setTimeout(() => {
          if (!trigger.matches(':hover')) {
            return;
          }

          node.classList.add(MODAL_OPEN_CLASS);

          const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
          document.body.style.paddingRight = `${scrollbarWidth}px`;
          document.documentElement.style.overflow = 'hidden';

          trigger.classList.add(NAV_ITEM_ACTIVE_CLASS);
          megamenu.hidden = false;
          repaint(megamenu);
          megamenu.classList.add(MEGAMENU_ACTIVE_CLASS);
          trigger.ariaExpanded = 'true';
          trigger.addEventListener('mouseleave', onLeave);
          megamenu.addEventListener('mouseleave', onLeave);

          dispatch('navigation-open', {
            label: trigger.textContent?.replace(/\n/g, '').trim() ?? 'Desktop',
            gender: getGender()
          });
        }, MEGAMENU_DELAY);
      });
    });
  };

  const initMobileMenuToggle = () => {
    document.addEventListener('mobile-menu:open', () => {
      mobileMenuToggle.classList.add(MOBILE_MENU_TOGGLE_OPEN_CLASS);
    });

    document.addEventListener('mobile-menu:close', () => {
      mobileMenuToggle.classList.remove(MOBILE_MENU_TOGGLE_OPEN_CLASS);
    });
  };

  window.addEventListener('scroll', handleScroll);

  handleHeaderModals();
  handleSearchModal();
  initMegamenus();
  initMobileMenuToggle();
});
